import { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { StatCard } from "../components/dashboard/StatCard";
import { DeskOverview } from "../components/dashboard/DeskOverview";
import { LeadImport } from "../components/leads/LeadImport";
import { UpcomingCallbacksWidget } from "../components/calendar/UpcomingCallbacksWidget";
import { useAuth } from "../hooks/useAuth";
import {
    Users,
    Headphones,
    Upload,
    Calendar,
    X
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Select } from "../components/ui/select";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { format } from "date-fns";
import { searchDashboard } from "../services/crmServices/dashboardService.js";
import { useSelector } from "react-redux";
import { selectLeadStatuses } from "../store/slices/leadStatusSlice";
import { toCamelCase, getFullName } from "../services/common/util.js";

export function Dashboard() {
    const { user } = useAuth();
    const leadStatuses = useSelector(selectLeadStatuses);
    const [timeRange, setTimeRange] = useState("month");
    const [dateRange, setDateRange] = useState({ startDate: null, endDate: null });
    const [customDateRange, setCustomDateRange] = useState({
        startDate: null,
        endDate: null
    });
    const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [stats, setStats] = useState({
        totalLeads: 0,
        totalAgents: 0
    });
    const [leads, setLeads] = useState([]);
    const [desks, setDesks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false); // Separate state for refreshing data
    const [allData, setAllData] = useState({});

    // Memoize the date range calculation to avoid unnecessary recalculations
    const calculateDateRange = useCallback((range) => {
        const now = new Date();
        let startDate = null, endDate = null;
        
        switch (range) {
            case "1hour": {
                endDate = new Date(now);
                startDate = new Date(now.getTime() - 1 * 60 * 60 * 1000);
                break;
            }
            case "6hours": {
                endDate = new Date(now);
                startDate = new Date(now.getTime() - 6 * 60 * 60 * 1000);
                break;
            }
            case "12hours": {
                endDate = new Date(now);
                startDate = new Date(now.getTime() - 12 * 60 * 60 * 1000);
                break;
            }
            case "24hours": {
                endDate = new Date(now);
                startDate = new Date(now.getTime() - 24 * 60 * 60 * 1000);
                break;
            }
            case "today": {
                startDate = new Date();
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date();
                endDate.setHours(24, 0, 0, 0);
                break;
            }
            case "week": {
                startDate = new Date(now);
                const day = now.getDay();
                const diffToMonday = day === 0 ? -6 : 1 - day;
                startDate.setDate(now.getDate() + diffToMonday);
                startDate.setHours(0, 0, 0, 0);
                endDate = new Date(startDate);
                endDate.setDate(endDate.getDate() + 7);
                break;
            }
            case "month": {
                startDate = new Date(now.getFullYear(), now.getMonth(), 1);
                endDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);
                break;
            }
            default:
                return null;
        }
        
        return { startDate, endDate };
    }, []);

    // Update date range when timeRange changes, but batch the state update
    useEffect(() => {
        if (timeRange !== "custom") {
            const dateRange = calculateDateRange(timeRange);
            if (dateRange) {
                // Batch state updates to prevent multiple re-renders
                setCustomDateRange(dateRange);
                setShowCustomDatePicker(false);
            }
        } else {
            setShowCustomDatePicker(true);
        }
    }, [timeRange, calculateDateRange]);

    // Memoize initPage to prevent recreation on every render
    const initPage = useCallback(async (isInitialLoad = false) => {
        const { startDate, endDate } = customDateRange;
        if (!startDate || !endDate) return;
        
        // Use loading for initial load, refreshing for subsequent updates
        if (isInitialLoad) {
            setLoading(true);
        } else {
            setRefreshing(true);
        }
        
        try {
            const formattedStartDate = startDate instanceof Date ? startDate.toISOString() : startDate;
            const formattedEndDate = endDate instanceof Date ? endDate.toISOString() : endDate;
            
            const res = await searchDashboard({ 
                timeRange: timeRange, 
                startDate: formattedStartDate, 
                endDate: formattedEndDate 
            });
            
            if (res.data) {
                setAllData(res.data);
            }
        } catch (error) {
            console.error("Error fetching dashboard data:", error);
        } finally {
            if (isInitialLoad) {
                setLoading(false);
            } else {
                setRefreshing(false);
            }
        }
    }, [customDateRange, timeRange]);

    // Initial load - wait for date range to be set first
    const hasInitialized = useRef(false);
    useEffect(() => {
        if (!hasInitialized.current && customDateRange.startDate && customDateRange.endDate) {
            hasInitialized.current = true;
            initPage(true); // Initial load
        }
    }, [customDateRange, initPage]);

    // Only fetch data when customDateRange actually changes (not on every render)
    const prevDateRangeRef = useRef(customDateRange);
    useEffect(() => {
        // Skip initial mount
        if (!prevDateRangeRef.current.startDate) {
            prevDateRangeRef.current = customDateRange;
            return;
        }
        
        // Only fetch if the date range actually changed
        const prevRange = prevDateRangeRef.current;
        const rangeChanged = 
            prevRange.startDate?.getTime() !== customDateRange.startDate?.getTime() ||
            prevRange.endDate?.getTime() !== customDateRange.endDate?.getTime();
        
        if (rangeChanged && customDateRange.startDate && customDateRange.endDate) {
            prevDateRangeRef.current = customDateRange;
            initPage(false); // Refresh, not initial load
        }
    }, [customDateRange, initPage]);

    const handleTimeRangeChange = useCallback((value) => {
        setTimeRange(value);
    }, []);

    const handleCustomDateChange = useCallback((field, date) => {
        setCustomDateRange((prev) => ({
            ...prev,
            [field]: date
        }));
    }, []);

    const clearCustomDateRange = () => {
        setCustomDateRange({ startDate: null, endDate: null });
        setTimeRange("month");
        setShowCustomDatePicker(false);
    };

    // Only show full loading on initial load
    if (loading && Object.keys(allData).length === 0) {
        return (
            <div className="max-w-[1600px] mx-auto px-4">
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-2 border-gray-200 border-t-red-600 mx-auto mb-3"></div>
                        <p className="text-sm text-gray-600 font-medium">Loading dashboard...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show different dashboard based on role
    // Keep content visible during refresh, just show a subtle indicator
    if (Object.keys(allData).length > 0 || !loading) {
        if (user?.position.role === "agent") {
            return (
                <div className="max-w-[1600px] mx-auto px-4">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
                            <p className="text-sm text-gray-500 mt-0.5">Your performance overview</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Select
                                options={[
                                    { label: "Last 1 Hour", value: "1hour" },
                                    { label: "Last 6 Hours", value: "6hours" },
                                    { label: "Last 12 Hours", value: "12hours" },
                                    { label: "Last 24 Hours", value: "24hours" },
                                    { label: "Today", value: "today" },
                                    { label: "This Week", value: "week" },
                                    { label: "This Month", value: "month" },
                                    { label: "Custom", value: "custom" }
                                ]}
                                value={timeRange}
                                onChange={handleTimeRangeChange}
                                className="w-40"
                            />
                            {timeRange === "custom" && (
                                <div className="flex items-center space-x-2">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-[140px] justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {customDateRange.startDate
                                                    ? format(customDateRange.startDate, "MMM dd, yyyy")
                                                    : "Start Date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={customDateRange.startDate}
                                                onSelect={(date) => handleCustomDateChange("startDate", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-[140px] justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {customDateRange.endDate
                                                    ? format(customDateRange.endDate, "MMM dd, yyyy")
                                                    : "End Date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={customDateRange.endDate}
                                                onSelect={(date) => handleCustomDateChange("endDate", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearCustomDateRange}
                                        title="Clear custom date range"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <StatCard 
                            title="My Leads" 
                            value={allData.leadsCount ?? 0} 
                            icon={Users}
                        />
                    </div>

                    {/* Upcoming Callbacks (agent) */}
                    <div className="mb-4">
                        <UpcomingCallbacksWidget limit={5} />
                    </div>

                    {/* Agent-specific lead breakdown */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <Card className="border-gray-200">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Lead Status Breakdown</CardTitle>
                            </CardHeader>
                            <CardContent>
                            <div className="space-y-2">
                                {leadStatuses && leadStatuses.length > 0 ? (
                                    leadStatuses
                                        .map((statusOption) => {
                                            const status = statusOption.value;
                                            const count = allData.leadStatusBreakdown?.[status] || 0;
                                            return { statusOption, count };
                                        })
                                        .filter(({ count }) => count > 0)
                                        .map(({ statusOption, count }) => (
                                            <div key={statusOption.value} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                                                <span className="text-sm text-gray-700">{statusOption.label}</span>
                                                <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                    {count}
                                                </span>
                                            </div>
                                        ))
                                ) : (
                                    allData.leadStatusBreakdown ? (
                                        Object.entries(allData.leadStatusBreakdown)
                                            .filter(([status, count]) => count > 0)
                                            .map(([status, count]) => (
                                                <div key={status} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
                                                    <span className="text-sm text-gray-700 capitalize">{status.replace(/_/g, " ")}</span>
                                                    <span className="text-sm font-semibold text-gray-900 tabular-nums">{count}</span>
                                                </div>
                                            ))
                                    ) : (
                                        <div className="text-sm text-gray-500 py-4 text-center">No leads found</div>
                                    )
                                )}
                            </div>
                            </CardContent>
                        </Card>

                        <Card className="border-gray-200">
                            <CardHeader className="pb-3">
                                <CardTitle className="text-sm font-semibold text-gray-900 uppercase tracking-wide">Performance Metrics</CardTitle>
                            </CardHeader>
                            <CardContent>
                            <div className="space-y-3">
                                {/* VoIP Call Statistics */}
                                <div className="pb-3 border-b border-gray-100">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">VoIP Calls</h4>
                                    <div className="space-y-1.5">
                                        {allData.voipCallStats ? (
                                            <>
                                                {/* Show all VoIP providers that user has access to */}
                                                {user.permissions?.voip?.commpeak && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-500">CommPeak</span>
                                                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                            {allData.voipCallStats.commpeak || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.permissions?.voip?.voiso && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-500">Voiso</span>
                                                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                            {allData.voipCallStats.voiso || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.permissions?.voip?.coperato && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-500">Coperato</span>
                                                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                            {allData.voipCallStats.coperato || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                {user.permissions?.voip?.squaretalk && (
                                                    <div className="flex justify-between items-center">
                                                        <span className="text-sm text-gray-500">SquareTalk</span>
                                                        <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                            {allData.voipCallStats.squaretalk || 0}
                                                        </span>
                                                    </div>
                                                )}
                                                <div className="flex justify-between items-center pt-2 border-t border-gray-200 mt-2">
                                                    <span className="text-sm font-semibold text-gray-700">Total</span>
                                                    <span className="text-base font-bold text-gray-900 tabular-nums">
                                                        {allData.voipCallStats.total || 0}
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="text-sm text-gray-500 text-center py-2">No data</div>
                                        )}
                                    </div>
                                </div>

                                {/* Conversion and Response Rates */}
                                <div className="pt-3">
                                    <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Conversion</h4>
                                    <div className="space-y-1.5">
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Conversion Rate</span>
                                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                {allData.metrics?.conversionRate?.toFixed(2) || 0}%
                                            </span>
                                        </div>
                                        <div className="flex justify-between items-center">
                                            <span className="text-sm text-gray-500">Response Rate</span>
                                            <span className="text-sm font-semibold text-gray-900 tabular-nums">
                                                {allData.metrics?.responseRate?.toFixed(2) || 0}%
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            );
        }

        if (user?.position?.role === "deskManager") {
            return (
                <div className="max-w-[1600px] mx-auto px-4">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">Dashboard</h1>
                            <p className="text-sm text-gray-500 mt-0.5">Desk performance overview</p>
                        </div>
                        <div className="flex items-center gap-2">
                            <Select
                                options={[
                                    { label: "Last 1 Hour", value: "1hour" },
                                    { label: "Last 6 Hours", value: "6hours" },
                                    { label: "Last 12 Hours", value: "12hours" },
                                    { label: "Last 24 Hours", value: "24hours" },
                                    { label: "Today", value: "today" },
                                    { label: "This Week", value: "week" },
                                    { label: "This Month", value: "month" },
                                    { label: "Custom", value: "custom" }
                                ]}
                                value={timeRange}
                                onChange={handleTimeRangeChange}
                                className="w-40"
                            />
                            {timeRange === "custom" && (
                                <div className="flex items-center space-x-2">
                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-[140px] justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {customDateRange.startDate
                                                    ? format(customDateRange.startDate, "MMM dd, yyyy")
                                                    : "Start Date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={customDateRange.startDate}
                                                onSelect={(date) => handleCustomDateChange("startDate", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Popover>
                                        <PopoverTrigger asChild>
                                            <Button
                                                variant="outline"
                                                className="w-[140px] justify-start text-left font-normal"
                                            >
                                                <Calendar className="mr-2 h-4 w-4" />
                                                {customDateRange.endDate
                                                    ? format(customDateRange.endDate, "MMM dd, yyyy")
                                                    : "End Date"}
                                            </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-auto p-0" align="start">
                                            <CalendarComponent
                                                mode="single"
                                                selected={customDateRange.endDate}
                                                onSelect={(date) => handleCustomDateChange("endDate", date)}
                                                initialFocus
                                            />
                                        </PopoverContent>
                                    </Popover>

                                    <Button
                                        variant="outline"
                                        size="icon"
                                        onClick={clearCustomDateRange}
                                        title="Clear custom date range"
                                    >
                                        <X className="h-4 w-4" />
                                    </Button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                        <StatCard
                            title="Team Agents"
                            value={allData.totalAgentsCount || 0}
                            icon={Users}
                        />
                        <StatCard 
                            title="Team Leads" 
                            value={allData.leadsCount || 0} 
                            icon={Users}
                        />
                    </div>

                    <DeskOverview desks={allData.desks || []} timeRange={timeRange} />

                    <div className="mt-4">
                        <UpcomingCallbacksWidget limit={5} />
                    </div>
                </div>
            );
        }

        // Admin Dashboard (default)
        return (
            <div className="max-w-[1600px] mx-auto px-4">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h1 className="text-xl font-semibold text-gray-900">
                                {user?.position?.role === "admin"
                                    ? "Admin Dashboard"
                                    : user.position.floor?.charAt(0).toUpperCase() + user.position.floor?.slice(1) + " Dashboard"}
                            </h1>
                            <p className="text-sm text-gray-500 mt-0.5">Overview and statistics</p>
                        </div>
                    <div className="flex items-center gap-2">
                        <Select
                            options={[
                                { label: "Last 1 Hour", value: "1hour" },
                                { label: "Last 6 Hours", value: "6hours" },
                                { label: "Last 12 Hours", value: "12hours" },
                                { label: "Last 24 Hours", value: "24hours" },
                                { label: "Today", value: "today" },
                                { label: "This Week", value: "week" },
                                { label: "This Month", value: "month" },
                                { label: "Custom", value: "custom" }
                            ]}
                            value={timeRange}
                            onChange={handleTimeRangeChange}
                            className="w-40"
                        />
                        {timeRange === "custom" && (
                            <div className="flex items-center space-x-2">
                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-[140px] justify-start text-left font-normal"
                                        >
                                            <Calendar className="mr-2 h-4 w-4" />
                                            {customDateRange.startDate
                                                ? format(customDateRange.startDate, "MMM dd, yyyy")
                                                : "Start Date"}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <CalendarComponent
                                            mode="single"
                                            selected={customDateRange.startDate}
                                            onSelect={(date) => handleCustomDateChange("startDate", date)}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>

                                <Popover>
                                    <PopoverTrigger asChild>
                                        <Button
                                            variant="outline"
                                            className="w-[140px] justify-start text-left font-normal"
                                        >
                                            <Calendar className="mr-2 h-4 w-4" />
                                            {customDateRange.endDate
                                                ? format(customDateRange.endDate, "MMM dd, yyyy")
                                                : "End Date"}
                                        </Button>
                                    </PopoverTrigger>
                                    <PopoverContent className="w-auto p-0" align="start">
                                        <CalendarComponent
                                            mode="single"
                                            selected={customDateRange.endDate}
                                            onSelect={(date) => handleCustomDateChange("endDate", date)}
                                            initialFocus
                                        />
                                    </PopoverContent>
                                </Popover>

                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={clearCustomDateRange}
                                    title="Clear custom date range"
                                >
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <StatCard 
                        title="Total Leads" 
                        value={allData.leadsCount || 0} 
                        icon={Users}
                    />
                    <StatCard
                        title="Total Agents"
                        value={allData.totalAgentsCount || 0}
                        icon={Headphones}
                    />
                </div>

                <DeskOverview desks={allData.desks || []} timeRange={timeRange} />

                <div className="mt-4">
                    <UpcomingCallbacksWidget limit={5} />
                </div>

                {showImport && <LeadImport onClose={() => setShowImport(false)} />}
            </div>
        );
    }
}
